import AudioPreview from 'components/UI/AudioPreview';
import { useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { IoClose, IoPause, IoPlay } from 'react-icons/io5';
import {
  formatIpawsAudioDuration,
  getIpawsAudioDuration,
  getIpawsAudioFileError,
  IPAWS_EAS_AUDIO_DURATION_ERROR,
  IPAWS_EAS_AUDIO_MAXIMUM_DURATION_SECONDS,
} from '../../../../utils/ipawsAudio.utils';

const IpawsAudioSection = ({ file, duration, onChange }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [isReading, setIsReading] = useState(false);

  const acceptFile = async (candidate) => {
    const fileError = getIpawsAudioFileError(candidate);
    if (fileError) {
      setError(fileError);
      return;
    }

    setIsReading(true);
    try {
      const nextDuration = await getIpawsAudioDuration(candidate);
      if (nextDuration > IPAWS_EAS_AUDIO_MAXIMUM_DURATION_SECONDS) {
        setError(IPAWS_EAS_AUDIO_DURATION_ERROR);
        return;
      }
      setError('');
      onChange({ file: candidate, duration: nextDuration });
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : 'The MP3 file could not be read.');
    } finally {
      setIsReading(false);
    }
  };

  const clearFile = () => {
    setError('');
    if (inputRef.current) inputRef.current.value = '';
    onChange({ file: null, duration: 0 });
  };

  return (
    <fieldset className="alert-creator-section alert-creator-section-ipaws border rounded-3 p-3 shadow-sm">
      <legend className="alert-creator-section-title d-flex align-items-center">Audio</legend>

      {!file ? (
        <div
          className="p-3 text-center"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            void acceptFile(event.dataTransfer.files?.[0]);
          }}
        >
          <FaCloudUploadAlt aria-hidden="true" size={28} />
          <p className="mb-1">Drag and drop an MP3 file here</p>
          <span className="d-block small mb-2">or</span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={isReading}
            onClick={() => inputRef.current?.click()}
          >
            {isReading ? 'Reading file…' : 'Select file'}
          </Button>
          <Form.Control
            ref={inputRef}
            type="file"
            accept=".mp3,audio/mpeg,audio/mp3"
            className="visually-hidden"
            aria-label="Select EAS broadcast MP3"
            onChange={(event) => void acceptFile(event.target.files?.[0])}
          />
          <small className="d-block mt-2">.mp3 · Maximum 2 minutes · Maximum 5 MB</small>
        </div>
      ) : (
        <div className="d-flex align-items-center gap-2 border rounded-3 p-2 flex-wrap">
          <div className="min-width-0 flex-grow-1">
            <strong className="d-block text-truncate">{file.name}</strong>
            <small>
              {formatIpawsAudioDuration(duration)} · {(file.size / (1024 * 1024)).toFixed(2)} MB
            </small>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline-danger"
            aria-label={`Remove ${file.name}`}
            onClick={clearFile}
          >
            <IoClose aria-hidden="true" size={18} />
          </Button>
          <div className="w-100 d-flex">
            <AudioPreview
              file={file}
              playButtonOverride={<IoPlay aria-hidden="true" size={20} />}
              pauseButtonOverride={<IoPause aria-hidden="true" size={20} />}
              buttonClassName="btn btn-primary d-inline-flex align-items-center justify-content-center p-0 flex-shrink-0"
              buttonStyle={{ width: 38, height: 38 }}
              ariaLabel={`Play or pause ${file.name}`}
              showTimeline
              durationSeconds={duration}
            />
          </div>
        </div>
      )}

      {error ? (
        <div className="text-danger small mt-2" role="alert">
          {error}
        </div>
      ) : null}
    </fieldset>
  );
};

export default IpawsAudioSection;
